import React, { useContext, useEffect, useRef, useState } from 'react'
import { CommentHeader, CommentItem, CommentList, CommentText, HistoryItemText, SectionTitle, InputComment, UserImage, WhatTime, InputCommentDiv } from '../style'
import { User } from 'lucide-react'
import axios from '../../../../config/axios'
import UserContext from '../../../../contexts/UserContext'
import styled from 'styled-components'

type CommentsOfOpProps =
  | { ticketId: number; serviceStageId?: never }
  | { serviceStageId: number; ticketId?: never }

interface UserSuggestion {
  id: number
  name: string
  picture?: string
}

const MentionDropdown = styled.ul`
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #dfe1e6;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  list-style: none;
  margin: 0;
  padding: 4px 0;
  z-index: 1200;
  max-height: 180px;
  overflow-y: auto;
`

const MentionItem = styled.li<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #172b4d;
  background: ${({ $active }) => ($active ? '#e8f6fc' : 'transparent')};

  &:hover {
    background: #f4f6f8;
  }
`

const MentionAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`

const MentionHighlight = styled.span`
  color: #0090D3;
  font-weight: 500;
`

const renderCommentWithMentions = (text: string) => {
  const parts = text.split(/(@\S+)/g)
  return parts.map((part, i) =>
    part.startsWith('@') ? (
      <MentionHighlight key={i}>{part}</MentionHighlight>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  )
}

const CommentsOfOp: React.FC<CommentsOfOpProps> = (props) => {
  const [comments, setComments] = useState<{ id: number; comment: string; createdAt: string; createdBy?: { id: number; name: string; picture?: string } }[]>([])
  const [comment, setComment] = useState<string>('')
  const [mentionedUserIds, setMentionedUserIds] = useState<number[]>([])
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([])
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const { user } = useContext(UserContext)
  const inputRef = useRef<HTMLInputElement>(null)

  const serviceStageId = 'serviceStageId' in props ? props.serviceStageId : undefined
  const ticketId = 'ticketId' in props ? props.ticketId : undefined
  const usesServiceStageComments = serviceStageId != null

  const resolveUserPicture = (userLike: any): string | undefined =>
    userLike?.picture || userLike?.avatar || userLike?.avatarUrl || userLike?.profilePicture || userLike?.image || userLike?.photo || undefined

  const normalizeComment = (rawComment: any) => {
    const sourceUser = rawComment?.createdBy || rawComment?.User || rawComment?.user || null
    const normalizedUser = sourceUser
      ? { id: sourceUser.id, name: sourceUser.name, picture: resolveUserPicture(sourceUser) }
      : undefined
    return { id: rawComment?.id, comment: rawComment?.comment, createdAt: rawComment?.createdAt, createdBy: normalizedUser }
  }

  const fetchComments = async () => {
    try {
      if (usesServiceStageComments) {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/from-app/comments/${serviceStageId}`)
        const data = Array.isArray(response.data) ? response.data : []
        setComments(data.map((item: any) => normalizeComment(item)))
        return
      }
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/comments`, { params: { ticketId } })
      if (response.data) {
        const data = Array.isArray(response.data?.data) ? response.data.data : []
        setComments(data.map((item: any) => normalizeComment(item)))
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetchComments()
  }, [ticketId, serviceStageId])

  const fetchUserSuggestions = async (query: string) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll`, {
        params: { search: query, loadQuantity: 8 },
      })
      const data = response.data?.data || response.data || []
      setSuggestions(Array.isArray(data) ? data.map((u: any) => ({ id: u.id, name: u.name, picture: resolveUserPicture(u) })) : [])
      setActiveIndex(0)
    } catch {
      setSuggestions([])
    }
  }

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setComment(value)

    const cursorPos = e.target.selectionStart ?? value.length
    const textUpToCursor = value.slice(0, cursorPos)
    const atMatch = textUpToCursor.match(/@(\w*)$/)

    if (atMatch) {
      setMentionQuery(atMatch[1])
      fetchUserSuggestions(atMatch[1])
    } else {
      setMentionQuery(null)
      setSuggestions([])
    }
  }

  const selectSuggestion = (suggestedUser: UserSuggestion) => {
    const cursorPos = inputRef.current?.selectionStart ?? comment.length
    const before = comment.slice(0, cursorPos)
    const after = comment.slice(cursorPos)
    const replaced = before.replace(/@(\w*)$/, `@${suggestedUser.name} `)
    setComment(replaced + after)
    setMentionedUserIds((prev) => prev.includes(suggestedUser.id) ? prev : [...prev, suggestedUser.id])
    setMentionQuery(null)
    setSuggestions([])
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => (i + 1) % suggestions.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        selectSuggestion(suggestions[activeIndex])
        return
      }
      if (e.key === 'Escape') {
        setMentionQuery(null)
        setSuggestions([])
        return
      }
    }
    if (e.key === 'Enter') {
      handleAddComment()
    }
  }

  const handleAddComment = async () => {
    if (!comment.trim()) return

    try {
      if (usesServiceStageComments) {
        await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/from-app/comments`, { comment, serviceStageId })
      } else {
        await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/comments`, {
          comment,
          ticketId,
          ...(mentionedUserIds.length > 0 && { mentionedUserIds }),
        })
      }
      setComment('')
      setMentionedUserIds([])
      await fetchComments()
      if (usesServiceStageComments) {
        window.dispatchEvent(new Event('pipeline-request-sync'))
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <>
      <SectionTitle>Comentários</SectionTitle>
      <CommentList>
        <InputCommentDiv>
          <UserImage biggest={true}>
            {resolveUserPicture(user)
              ? <img src={resolveUserPicture(user)} alt={user.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              : <User size={20} />}
          </UserImage>
          <InputWrapper>
            {suggestions.length > 0 && mentionQuery !== null && (
              <MentionDropdown>
                {suggestions.map((s, idx) => (
                  <MentionItem
                    key={s.id}
                    $active={idx === activeIndex}
                    onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s) }}
                  >
                    <MentionAvatar>
                      {s.picture
                        ? <img src={s.picture} alt={s.name} />
                        : <User size={14} />}
                    </MentionAvatar>
                    {s.name}
                  </MentionItem>
                ))}
              </MentionDropdown>
            )}
            <InputComment
              ref={inputRef}
              placeholder='Escreva um comentário... (@ para mencionar)'
              onKeyDown={handleKeyDown}
              onChange={handleCommentChange}
              value={comment}
            />
          </InputWrapper>
        </InputCommentDiv>
        {comments.map((c) => (
          <CommentItem key={c.id}>
            <CommentHeader>
              <UserImage>
                {resolveUserPicture(c.createdBy)
                  ? <img src={resolveUserPicture(c.createdBy)} alt={c.createdBy?.name} style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }} />
                  : <User size={20} />}
              </UserImage>
              <HistoryItemText><span>{c.createdBy?.name}</span></HistoryItemText>
            </CommentHeader>
            <CommentText>{renderCommentWithMentions(c.comment)}</CommentText>
            <WhatTime>{new Date(c.createdAt).toLocaleString()}</WhatTime>
          </CommentItem>
        ))}
      </CommentList>
    </>
  )
}

export default CommentsOfOp
